import React from 'react';
import { Info, AlertTriangle, CheckCircle2, Lightbulb } from 'lucide-react';

/**
 * InsightCard — the single "Tier A" insight surface for educational callouts.
 *
 * Replaces the legacy flat-pastel boxes (bg-amber-50 / bg-blue-50 + thin border). The card surface
 * stays white with a soft shadow; tone drives ONLY the accent rail + icon colour, so colour reads as
 * a signal rather than wallpaper. This is the shared primitive referenced as X3 in
 * docs/UI_UX_HERO_PATH_AUDIT.md. (The live "What's happening?" narrative panel is a separate piece.)
 *
 * Props:
 *   tone     'info' | 'caution' | 'positive' | 'neutral'  (default 'info')
 *   heading  optional bold heading line
 *   icon     optional lucide icon component to override the tone default
 *   children body content
 */
const TONES = {
    info: { rail: 'border-l-brand-blue', icon: 'text-brand-blue', chip: 'bg-blue-50', Icon: Info },
    caution: { rail: 'border-l-amber-400', icon: 'text-amber-500', chip: 'bg-amber-50', Icon: AlertTriangle },
    positive: { rail: 'border-l-emerald-400', icon: 'text-emerald-500', chip: 'bg-emerald-50', Icon: CheckCircle2 },
    neutral: { rail: 'border-l-slate-300', icon: 'text-slate-500', chip: 'bg-slate-100', Icon: Lightbulb },
};

type IconComponent = React.ComponentType<{ size?: number; strokeWidth?: number; className?: string }>;

interface InsightCardProps {
    tone?: keyof typeof TONES;
    heading?: React.ReactNode;
    icon?: IconComponent;
    children: React.ReactNode;
    className?: string;
}

const InsightCard = ({ tone = 'info', heading, icon, children, className = '' }: InsightCardProps) => {
    const t = TONES[tone] || TONES.info;
    const Icon = icon || t.Icon;
    return (
        <div className={`bg-white rounded-2xl border border-slate-200/70 border-l-4 ${t.rail} shadow-card p-5 ${className}`}>
            <div className="flex items-start gap-3">
                <span className={`flex-shrink-0 w-9 h-9 rounded-xl ${t.chip} ${t.icon} flex items-center justify-center`}>
                    <Icon size={18} strokeWidth={2.2} />
                </span>
                <div className="min-w-0 flex-1">
                    {heading && <p className="text-sm font-semibold text-slate-900 mb-1">{heading}</p>}
                    <div className="text-sm leading-relaxed text-slate-600">{children}</div>
                </div>
            </div>
        </div>
    );
};

export default InsightCard;
